import React from 'react';
import PropTypes from 'prop-types';

import {
  trimBothSides,
  SORT_ORDER,
  recapEventNamePieces,
  sortAncestorsByWords,
  transformAncestors,
} from '@stubhub/header-helpers';
import FormattedLink from '@stubhub/react-formatted-link';

// CSS
import './index.scss';

/**
 * Functional component shows event name
 * @param {String} eventName
 * @param {Array} performers
 */
const EventName = ({eventName, performers = [], groupings = []}) => {
  // Order performers by names wth greatest number of words in the name string.
  const transformedPerformers = transformAncestors(performers);
  const sortedPerformers = sortAncestorsByWords(transformedPerformers, SORT_ORDER.DESCENDING);

  // Order groupings by names wth lowest number of words in the name string.
  const transformedGroupings = transformAncestors(groupings);
  const sortedGroupings = sortAncestorsByWords(transformedGroupings, SORT_ORDER.ASCENDING);

  const cleanPerformerNames = trimBothSides(sortedPerformers.map((element) => element.cleanName))?.filter(
    (element) => element !== '' && element !== ' '
  );
  const cleanGroupingNames = trimBothSides(sortedGroupings.map((element) => element.cleanName))?.filter(
    (element) => element !== '' && element !== ' '
  );

  // Create a map of the starting locations within the event name to the performer/grouping data.
  const eventNamePieces = {};
  // Iterate through all of the performers and groupings for which we want to link.
  recapEventNamePieces(eventName, eventNamePieces, sortedGroupings, cleanGroupingNames);
  recapEventNamePieces(eventName, eventNamePieces, sortedPerformers, cleanPerformerNames);

  // Iterate through all of the pieces of the event name that are performers or groupings for which we want to link.
  const elements = [];
  const eventNamePiecesKeys = Object.keys(eventNamePieces);
  eventNamePiecesKeys.forEach((pieceIndex, index) => {
    const startIndex =
      index === 0
        ? 0
        : parseInt(eventNamePiecesKeys[index - 1]) + eventNamePieces[eventNamePiecesKeys[index - 1]].cleanName.length;

    // Add a span for the piece of the event name preceding the performer/grouping link.
    elements.push(<span key={`span-${pieceIndex}`}>{eventName.substring(startIndex, pieceIndex)}</span>);

    // Add a link for the performer/grouping.
    elements.push(
      <FormattedLink key={`link-${pieceIndex}`} href={eventNamePieces[pieceIndex].webURI}>
        {eventNamePieces[pieceIndex].cleanName}
      </FormattedLink>
    );
  });

  // Add a span for the rest of the event name once we've exhausted all of the performer/grouping link pieces.
  const startIndex =
    eventNamePiecesKeys.length === 0
      ? 0
      : parseInt(eventNamePiecesKeys[eventNamePiecesKeys.length - 1]) +
        eventNamePieces[eventNamePiecesKeys[eventNamePiecesKeys.length - 1]].cleanName.length;
  elements.push(<span key="last-span">{eventName.substring(startIndex)}</span>);

  return <h1 className="EventName__eventName">{elements}</h1>;
};

EventName.propTypes = {
  /** Event Name for EX:Texas Rangers at San Francisco Giants*/
  eventName: PropTypes.string.isRequired,

  /** Array of performers of the event */
  performers: PropTypes.arrayOf(PropTypes.object),

  /** Array of groupings of the event */
  groupings: PropTypes.arrayOf(PropTypes.object),
};

export default EventName;
